import { useMemo } from 'react';
import { connect } from 'react-redux';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { Link } from 'react-router-dom';
import { Edit } from '@mui/icons-material';
import { unique } from '../../utils/collections';
import EquipmentLoader from '../loaders/EquipmentLoader';
import { getBuildingsEntities } from '../../redux/selectors/buildings';
import {
  LIST_DELIMITER,
  MISSING_VALUE,
  tableSelectionOptions,
  defaultTableOptions,
  defaultTableInitialState,
} from '../util/data-table';
import { DateCell, CellList } from '../util/DataTableCells';
import { buildStagingLink } from '../staging/staging';
import { pointTopicId } from '../staging/columns';

const PointsTable = ({
  points,
  equipment,
  buildingsById,
  orgsById,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  pointSelection,
  onRowSelectionChange,
  pointTypesById,
  unitsById,
  deployments,
}) => {
  const data = useMemo(
    () =>
      points
        .values()
        .map((p) => ({
          building: buildingsById[p.buildingId],
          org: orgsById[buildingsById[p.buildingId].orgId],
          ...p,
        }))
        .toArray(),
    [orgsById, points, buildingsById]
  );

  const observedEquipmentNameIds = useMemo(
    () =>
      unique(
        equipment
          .values()
          .map((e) => ({ label: e.name, value: e.id }))
          .toArray()
      ),
    [equipment]
  );

  const columns = useMemo(() => {
    const pointType = ({ row, renderedCellValue }) => (
      <>
        {pointTypesById[renderedCellValue]?.name ?? renderedCellValue}
        <Link
          to={buildStagingLink(row.original.buildingId, 'points', {
            [pointTopicId]: row.original.topic,
          })}
        >
          <span>
            <Edit color="action" />
          </span>
        </Link>
      </>
    );
    return [
      // TODO: group shared column defs in shared file
      {
        id: 'o_name',
        accessorFn: ({ org }) => org?.name ?? MISSING_VALUE,
        header: 'Organization Name',
        filterVariant: 'multi-select',
      },
      {
        id: 'b_name',
        header: 'Building Name',
        accessorFn: ({ building }) => building?.name,
        filterVariant: 'autocomplete',
      },
      {
        id: 'p_eids',
        header: 'Equipment Names',
        accessorFn: (p) => p?.equipment.map((e) => e.id),
        muiFilterAutocompleteProps: {
          getOptionKey: (e) => e.id,
          getOptionLabel: (e) => e.name,
        },
        filterVariant: 'multi-select',
        filterSelectOptions: observedEquipmentNameIds,
        Cell: ({ row }) => CellList(row.original.equipment.map((e) => e.name)),
      },
      { id: 'p_name', accessorKey: 'name', header: 'Point Name', filterVariant: 'autocomplete' },
      {
        id: 'p_type',
        header: 'Point Type',
        accessorFn: ({ pointTypeId }) => pointTypesById[pointTypeId]?.name ?? MISSING_VALUE,
        filterVariant: 'multi-select',
        Cell: pointType,
      },
      { id: 'p_value', accessorFn: ({ value }) => value || 'n/a', header: 'Last Known Value' },
      {
        id: 'p_units',
        accessorFn: ({ rawUnitId }) => unitsById[rawUnitId]?.name_abbr ?? MISSING_VALUE,
        header: 'Units',
        filterVariant: 'multi-select',
      },
      {
        id: 'p_lastIngest',
        header: 'Last Data Ingested',
        sortingFn: 'datetime',
        enableColumnFilter: false,
        accessorFn: ({ lastUpdatedTs }) =>
          !lastUpdatedTs ? MISSING_VALUE : new Date(lastUpdatedTs),
        Cell: DateCell,
      },
      {
        id: 'p_object_id',
        header: 'Object Id',
        accessorFn: ({ objectId }) => objectId,
      },
      {
        id: 'p_device_id',
        header: 'Device Id',
        accessorFn: ({ device }) => device?.device_id ?? MISSING_VALUE,
      },
      {
        id: 'p_device_name',
        header: 'Device Name',
        accessorFn: ({ device }) => device?.name ?? MISSING_VALUE,
      },
      {
        id: 'p_tags',
        header: 'Haystack Tags',
        accessorFn: ({ tags }) => (tags?.toSorted() ?? []).join(LIST_DELIMITER),
      },
      {
        id: 'p_labels',
        header: 'Custom Labels',
        accessorFn: ({ labels }) => (labels?.toSorted() ?? []).join(LIST_DELIMITER),
      },
      {
        id: 'p_deployment',
        header: 'Deployment',
        accessorFn: ({ device }) => deployments?.[device?.deployment_id]?.name ?? MISSING_VALUE,
      },
      {
        id: 'p_subdeployment',
        header: 'Subdeployment',
        accessorFn: ({ device }) => device?.subdeployment_type ?? MISSING_VALUE,
      },
    ];
  }, [pointTypesById, unitsById, deployments, observedEquipmentNameIds]);

  const showBuildings = Object.keys(buildingsById).length > 1;
  const showOrgs =
    showBuildings && unique(Object.values(buildingsById).map((b) => b.orgId)).length <= 1;

  const columnIds = useMemo(() => columns.map(({ id }) => id), [columns]);
  const pointSorting = useMemo(
    () => sorting.filter(({ id }) => columnIds.includes(id)),
    [sorting, columnIds]
  );
  const pointColumnFilters = useMemo(
    () =>
      columnFilters
        .filter(({ id }) => columnIds.includes(id))
        .map((cf) => {
          // If observedEquipmentNameIds isn't populated yet, don't try to filter (MRT fails to initialize the CFs right for p_eids for some reason
          if (cf.id === 'p_eids' && observedEquipmentNameIds.length !== 0) {
            const validEids = new Set(observedEquipmentNameIds.map(({ value }) => value));
            const value = cf.value.filter((eid) => validEids.has(eid));
            return value.length === 0
              ? {}
              : { id: 'p_eids', value: cf.value.filter((eid) => validEids.has(eid)) };
          }
          return cf;
        })
        .filter((cf) => Object.keys(cf).length > 0),
    [columnFilters, columnIds, observedEquipmentNameIds]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    ...tableSelectionOptions,
    // https://onboard-data.slack.com/archives/C01JX53PBJ5/p1780328209112699
    enableGlobalFilter: false,
    columns,
    data,
    initialState: {
      ...defaultTableInitialState({
        columnFilters: pointColumnFilters,
        sorting: pointSorting,
        hiddenColumns: [
          showOrgs ? 'o_name' : null,
          showBuildings ? 'b_name' : null,
          'p_device_id',
          'p_device_name',
          'p_object_id',
          'p_tags',
          'p_labels',
          'p_deployment',
          'p_subdeployment',
        ],
      }),
    },
    onRowSelectionChange,
    // sorting
    onSortingChange,
    // filtering
    onColumnFiltersChange,
    state: {
      sorting: pointSorting,
      columnFilters: pointColumnFilters,
      rowSelection: pointSelection,
      showAlertBanner: true,
    },
  });

  if (Object.keys(buildingsById).length <= 0) return 'Too few buildings';
  if (Object.keys(buildingsById).length >= 10) return 'Too many buildings';
  return (
    <div>
      {Object.keys(buildingsById).map((bid) => (
        // EquipmentLoader also loads points
        <EquipmentLoader key={bid} buildingId={bid} getDevices />
      ))}
      <MaterialReactTable table={mrt} />
    </div>
  );
};

const mapStateToProps = (state, { buildingsById, orgsById }) => {
  const {
    entities: {
      deployments: { deployments },
    },
  } = state;
  return {
    ...getBuildingsEntities(state, Object.keys(buildingsById)),
    buildingsById,
    orgsById,
    pointTypesById: state.entities.pointTypes.byId || {},
    unitsById: state.entities.units.byId || {},
    deployments,
  };
};

// eslint-disable-next-line no-unused-vars
const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(PointsTable);
